import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Scanner as ScannerIcon,
} from '@mui/icons-material';
import { healthFromAgentDocument } from '../../../utils/agentHealthModel';
import {
  agentKindLabel,
  findSegmentForAgent,
  formatAgentLanIdentity,
  formatOfflineAge,
  getAgentDisplayName,
  getEmbeddedPlatformLabel,
  getSegmentDisplayCidr,
  hasAgentHeartbeat,
  isAgentOnline,
  isDismissibleStaleAgent,
  isEmbeddedEdgeRow,
  nextAgentSort,
  scanDisabledReason,
  sortAgents,
  toMillis,
} from './segmentAgentsTable';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function formatHeartbeat(value) {
  const ms = toMillis(value);
  return ms ? new Date(ms).toLocaleString() : 'Never';
}

/**
 * Single agents table — LAN workstation agents or embedded edge agents (superadmin toggle).
 * Page scrolls naturally; no nested max-height scroll regions.
 */
const UnifiedAgentsTable = ({
  agents,
  viewMode,
  segments,
  siteMap,
  showTenantColumn,
  showStaleToggle,
  onEditAgent,
  onDeleteAgent,
  onScanAgent,
  editLoadingAgentId,
  deletingAgentId,
  scanningAgents,
}) => {
  const [sort, setSort] = useState({ key: 'online', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showStaleAgents, setShowStaleAgents] = useState(false);

  const isLanOnly = viewMode === 'lan';
  const isEmbeddedOnly = viewMode === 'embedded';
  const isCombined = viewMode === 'all';

  const { activeAgents, staleAgents } = useMemo(() => {
    const enriched = (agents || []).map((agent) => {
      const embeddedRow = isCombined
        ? isEmbeddedEdgeRow(agent, segments)
        : isEmbeddedOnly;
      return {
        ...agent,
        siteLabel: agent.siteId ? (siteMap?.[agent.siteId] || agent.siteId) : '',
        segmentDoc: embeddedRow ? null : findSegmentForAgent(agent, segments),
        embeddedRow,
      };
    });
    const sorted = sortAgents(enriched, sort.key, sort.dir);
    if (isEmbeddedOnly || !showStaleToggle) {
      return { activeAgents: sorted, staleAgents: [] };
    }
    const active = [];
    const stale = [];
    sorted.forEach((agent) => {
      if (isDismissibleStaleAgent(agent, segments)) {
        stale.push(agent);
      } else {
        active.push(agent);
      }
    });
    return { activeAgents: active, staleAgents: stale };
  }, [agents, isCombined, isEmbeddedOnly, segments, showStaleToggle, siteMap, sort.dir, sort.key]);

  const visibleAgents = showStaleAgents ? [...activeAgents, ...staleAgents] : activeAgents;

  const pagedAgents = useMemo(() => {
    const start = page * rowsPerPage;
    return visibleAgents.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, visibleAgents]);

  const columns = useMemo(() => {
    const base = [
      { key: 'name', label: 'Name' },
      { key: 'agentId', label: 'Agent ID' },
    ];
    if (showTenantColumn) {
      base.push({ key: 'tenant', label: 'Tenant' });
    }
    if (isCombined) {
      base.push(
        { key: 'kind', label: 'Type' },
        { key: 'segment', label: 'LAN' },
        { key: 'site', label: 'Site' },
        { key: 'deviceId', label: 'Device ID' },
        { key: 'platform', label: 'Platform' }
      );
    } else if (isLanOnly) {
      base.push(
        { key: 'segment', label: 'LAN' },
        { key: 'site', label: 'Site' },
        { key: 'runtimeClass', label: 'Runtime' }
      );
    } else {
      base.push(
        { key: 'deviceId', label: 'Device ID' },
        { key: 'platform', label: 'Platform' }
      );
    }
    base.push({ key: 'online', label: 'Status' }, { key: 'lastHeartbeat', label: 'Last Heartbeat' });
    return base;
  }, [isCombined, isLanOnly, showTenantColumn]);

  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyLabel = isCombined
    ? 'No LAN or embedded agents for this scope.'
    : isLanOnly
      ? 'No LAN / workstation agents for this scope.'
      : 'No embedded / edge agents for this scope.';

  return (
    <Box>
      {!isEmbeddedOnly && staleAgents.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Button size="small" variant="text" onClick={() => setShowStaleAgents((v) => !v)}>
            {showStaleAgents
              ? `Hide ${staleAgents.length} inactive agent${staleAgents.length === 1 ? '' : 's'}`
              : `Show ${staleAgents.length} inactive agent${staleAgents.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sortDirection={sort.key === col.key ? sort.dir : false}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  <TableSortLabel
                    active={sort.key === col.key}
                    direction={sort.key === col.key ? sort.dir : 'asc'}
                    onClick={() => {
                      setSort((prev) => nextAgentSort(prev, col.key));
                      setPage(0);
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedAgents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {emptyLabel}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedAgents.map((agent) => {
                const online = isAgentOnline(agent);
                const displayName = getAgentDisplayName(agent);
                const embeddedRow = Boolean(agent.embeddedRow);
                const segment = agent.segmentDoc;
                const segmentCidr = segment ? getSegmentDisplayCidr(segment) : '';
                const stale = isDismissibleStaleAgent(agent, segments);
                const ghost = !hasAgentHeartbeat(agent);
                const scanReason = segment ? scanDisabledReason(agent, segment) : 'N/A for embedded agents';

                return (
                  <TableRow
                    key={`${agent.tenantId || 't'}-${agent.agentId}`}
                    sx={{
                      backgroundColor: stale ? 'action.selected' : 'inherit',
                      opacity: stale ? 0.85 : 1,
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {displayName || 'Unnamed'}
                        </Typography>
                        {isEmbeddedOnly && (
                          <Chip label="Embedded" size="small" color="info" variant="outlined" />
                        )}
                        <Chip label={agentKindLabel(agent)} size="small" variant="outlined" />
                        <Chip
                          label={healthFromAgentDocument(agent).state}
                          size="small"
                          color={healthFromAgentDocument(agent).state === 'online' ? 'success' : healthFromAgentDocument(agent).state === 'degraded' ? 'warning' : healthFromAgentDocument(agent).state === 'dormant' ? 'default' : 'error'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {agent.agentId}
                      </Typography>
                    </TableCell>
                    {showTenantColumn && (
                      <TableCell>
                        <Typography variant="body2">{agent.tenantName || agent.tenantId || '—'}</Typography>
                      </TableCell>
                    )}
                    {isCombined ? (
                      <>
                        <TableCell>
                          <Chip
                            label={embeddedRow ? 'Embedded' : 'LAN'}
                            size="small"
                            color={embeddedRow ? 'info' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {embeddedRow
                              ? '—'
                              : formatAgentLanIdentity(agent, segmentCidr) || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{embeddedRow ? '—' : agent.siteLabel || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {embeddedRow ? agent.deviceId || '—' : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {embeddedRow ? getEmbeddedPlatformLabel(agent) : agent.runtimeClass || '—'}
                        </TableCell>
                      </>
                    ) : isLanOnly ? (
                      <>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {formatAgentLanIdentity(agent, segmentCidr) || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{agent.siteLabel || '—'}</TableCell>
                        <TableCell>{agent.runtimeClass || '—'}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {agent.deviceId || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{getEmbeddedPlatformLabel(agent)}</TableCell>
                      </>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          label={online ? 'Online' : 'Offline'}
                          color={online ? 'success' : 'error'}
                          size="small"
                        />
                        {(!online || ghost) && (
                          <Chip
                            label={ghost ? 'No heartbeat' : formatOfflineAge(agent)}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatHeartbeat(agent.lastHeartbeat)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Tooltip title="Edit agent">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onEditAgent(agent.agentId)}
                              disabled={editLoadingAgentId === agent.agentId}
                            >
                              {editLoadingAgentId === agent.agentId ? (
                                <CircularProgress size={18} />
                              ) : (
                                <EditIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!embeddedRow && segment && (
                          <Tooltip title={scanReason || `Scan ${segmentCidr}`}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onScanAgent(segment, agent.agentId)}
                                disabled={Boolean(scanReason) || scanningAgents?.[agent.agentId]}
                              >
                                {scanningAgents?.[agent.agentId] ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <ScannerIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete agent">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDeleteAgent(agent.agentId)}
                              disabled={deletingAgentId === agent.agentId}
                            >
                              {deletingAgentId === agent.agentId ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {visibleAgents.length > 0 && (
        <TablePagination
          component="div"
          count={visibleAgents.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        />
      )}
    </Box>
  );
};

export default UnifiedAgentsTable;
